import { getFormattingLocale } from '@/composables/useLocale';
import { i18n } from '@/i18n/translations';
import { type IWeekNumberSettings, resolveWeekNumber } from '@/utils/schoolYear.utils';
import { now } from 'common/utils/date.utils';
import { endOfWeek, format, startOfWeek, subWeeks } from 'date-fns';

const ISO_WEEK_START = 1;
const RANGE_SEPARATOR_PATTERN = /\s*–\s*/u;

const { t } = i18n.global;

interface IDateRangeFormatter extends Intl.DateTimeFormat {
  formatRange(startDate: Date, endDate: Date): string;
}

const parseLocalCalendarDate = (value: string | Date): Date => {
  if (value instanceof Date) return new Date(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
};

const formatTimelineWeekRange = (weekStart: Date, weekEnd: Date, referenceDate: Date): string => {
  const includeYear =
    weekStart.getFullYear() !== referenceDate.getFullYear() || weekEnd.getFullYear() !== referenceDate.getFullYear();
  const formatter = new Intl.DateTimeFormat(getFormattingLocale(), {
    day: 'numeric',
    month: 'long',
    ...(includeYear ? { year: 'numeric' } : {}),
  }) as IDateRangeFormatter;

  // Intl uses locale-specific spacing around range separators. The timeline design intentionally
  // renders the compact variant in every locale while Intl still owns date order and punctuation.
  return formatter.formatRange(weekStart, weekEnd).replace(RANGE_SEPARATOR_PATTERN, '–');
};

export const dateToTimelineDayKey = (value: string | Date): string =>
  format(parseLocalCalendarDate(value), 'yyyy-MM-dd');

export const dateToTimelineWeekKey = (value: string | Date): string =>
  format(startOfWeek(parseLocalCalendarDate(value), { weekStartsOn: ISO_WEEK_START }), 'yyyy-MM-dd');

export interface ITimelineWeekLabel {
  label: string;
  weekNumber: string;
}

export const getTimelineWeekLabel = (
  value: string | Date,
  weekNumberSettings: IWeekNumberSettings,
): ITimelineWeekLabel => {
  const referenceDate = now();
  const weekStart = startOfWeek(parseLocalCalendarDate(value), { weekStartsOn: ISO_WEEK_START });
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: ISO_WEEK_START });
  const currentWeekKey = dateToTimelineWeekKey(referenceDate);
  const weekKey = dateToTimelineWeekKey(weekStart);
  const lastWeekKey = dateToTimelineWeekKey(subWeeks(referenceDate, 1));

  let label = formatTimelineWeekRange(weekStart, weekEnd, referenceDate);
  if (weekKey === currentWeekKey) label = t('timeline.thisWeek');
  else if (weekKey === lastWeekKey) label = t('timeline.lastWeek');

  const { representation, week } = resolveWeekNumber(weekStart, weekNumberSettings);
  return {
    label,
    weekNumber: t(`timeline.${representation}`, { week }),
  };
};
